/**
 * Tiny keyboard-shortcut helpers. Shortcuts are written as `"mod+k"`,
 * `"mod+shift+o"`, etc. — `mod` is ⌘ on macOS and Ctrl elsewhere. Used for both
 * matching key events and rendering hints (see `Kbd`).
 */
const isMac =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform);

interface ParsedShortcut {
  mod: boolean;
  shift: boolean;
  alt: boolean;
  key: string;
}

function parse(shortcut: string): ParsedShortcut {
  const parts = shortcut.toLowerCase().split("+");
  return {
    mod: parts.includes("mod"),
    shift: parts.includes("shift"),
    alt: parts.includes("alt"),
    key: parts[parts.length - 1] ?? "",
  };
}

export function matchShortcut(event: KeyboardEvent, shortcut: string): boolean {
  const parsed = parse(shortcut);
  const modPressed = isMac ? event.metaKey : event.ctrlKey;
  return (
    modPressed === parsed.mod &&
    event.shiftKey === parsed.shift &&
    event.altKey === parsed.alt &&
    event.key.toLowerCase() === parsed.key
  );
}

/** Render-ready tokens for a shortcut, e.g. `["⌘", "K"]` or `["Ctrl", "K"]`. */
export function formatShortcut(shortcut: string): string[] {
  const parsed = parse(shortcut);
  const tokens: string[] = [];
  if (parsed.mod) tokens.push(isMac ? "⌘" : "Ctrl");
  if (parsed.alt) tokens.push(isMac ? "⌥" : "Alt");
  if (parsed.shift) tokens.push(isMac ? "⇧" : "Shift");
  tokens.push(parsed.key.length === 1 ? parsed.key.toUpperCase() : parsed.key);
  return tokens;
}

/**
 * Render-ready keycaps for a leader chord written as space-separated keys, e.g.
 * `"g i"` → `["G", "I"]` (pressed in sequence, not together).
 */
export function formatChord(chord: string): string[] {
  return chord
    .trim()
    .split(/\s+/)
    .map((key) => (key.length === 1 ? key.toUpperCase() : key));
}
